import { useState, useEffect, useCallback, useRef } from 'react'
import { adminService } from '../../services/api'
import { Upload, X, ArrowUp, ArrowDown, Loader2, Image as ImageIcon, Download } from 'lucide-react'
import { useToast } from '../../hooks/useToast'
import { downloadOnePhoto, downloadPhotosZip, photoFileName, zipFileName } from './downloadProductPhotos'
import './ProductImageManager.css'

const MAX_FILE_BYTES = 5 * 1024 * 1024

/**
 * Gerencia imagens do produto: múltiplos arquivos, drag-and-drop, sem teto fixo de quantidade.
 */
function ProductImageManager({ productId, productLabel = '', editable = true, onImagesChange }) {
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)
  const [zipping, setZipping] = useState(false)
  const fileInputRef = useRef(null)
  const { showToast } = useToast()

  const loadImages = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminService.getProductImages(productId)
      if (response.success) {
        setImages(response.data || [])
      }
    } catch (err) {
      console.error('Erro ao carregar imagens:', err)
      setError('Erro ao carregar imagens')
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    if (productId) {
      loadImages()
    }
  }, [productId, loadImages])

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean)
    if (!files.length) return

    const invalidType = files.find((f) => !f.type?.startsWith('image/'))
    if (invalidType) {
      setError('Só é permitido enviar arquivos de imagem')
      return
    }

    const tooLarge = files.find((f) => f.size > MAX_FILE_BYTES)
    if (tooLarge) {
      setError(`Cada imagem deve ter no máximo 5MB (${tooLarge.name})`)
      return
    }

    try {
      setUploading(true)
      setError(null)
      let successCount = 0
      let orderBase = images.length

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i]
        setUploadProgress({ current: i + 1, total: files.length, name: file.name })

        const uploadResponse = await adminService.uploadImage(file)
        if (!uploadResponse.success) {
          throw new Error(
            uploadResponse.message || `Erro ao enviar ${file.name}`
          )
        }

        const addResponse = await adminService.addProductImage(
          productId,
          uploadResponse.data.url,
          orderBase + i
        )

        if (!addResponse.success) {
          throw new Error(
            addResponse.message || `Erro ao vincular ${file.name}`
          )
        }
        successCount += 1
      }

      await loadImages()
      onImagesChange?.()
      showToast(
        successCount === 1
          ? '✓ Imagem adicionada com sucesso!'
          : `✓ ${successCount} imagens adicionadas com sucesso!`,
        'success'
      )
    } catch (err) {
      console.error('Erro ao fazer upload:', err)
      setError(
        err.response?.data?.message ||
          err.message ||
          'Erro ao fazer upload da imagem'
      )
      await loadImages()
      onImagesChange?.()
    } finally {
      setUploading(false)
      setUploadProgress(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleImageUpload = async (e) => {
    await uploadFiles(e.target.files)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!editable || uploading) return
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (!editable || uploading) return
    await uploadFiles(e.dataTransfer?.files)
  }

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Deseja realmente remover esta imagem?')) {
      return
    }

    try {
      setError(null)
      const response = await adminService.deleteProductImage(productId, imageId)
      if (response.success) {
        await loadImages()
        onImagesChange?.()
        showToast('✓ Imagem removida com sucesso!', 'success')
      } else {
        throw new Error(response.message || 'Erro ao remover imagem')
      }
    } catch (err) {
      console.error('Erro ao remover imagem:', err)
      setError(
        err.response?.data?.message || err.message || 'Erro ao remover imagem'
      )
    }
  }

  const handleMoveImage = async (imageId, direction) => {
    const image = images.find((img) => img.id === imageId)
    if (!image) return

    const currentIndex = image.display_order
    const newOrder = direction === 'up' ? currentIndex - 1 : currentIndex + 1

    if (newOrder < 0 || newOrder >= images.length) return

    try {
      setError(null)
      const response = await adminService.updateImageOrder(
        productId,
        imageId,
        newOrder
      )
      if (response.success) {
        await loadImages()
        onImagesChange?.()
      } else {
        throw new Error(response.message || 'Erro ao atualizar ordem')
      }
    } catch (err) {
      console.error('Erro ao atualizar ordem:', err)
      setError(
        err.response?.data?.message ||
          err.message ||
          'Erro ao atualizar ordem'
      )
    }
  }

  const fileLabel = productLabel || `produto-${productId}`

  const handleDownloadOne = async (image, index) => {
    if (!image?.image_url || downloadingId || zipping) return
    setDownloadingId(image.id)
    setError(null)
    try {
      await downloadOnePhoto(
        image.image_url,
        photoFileName(fileLabel, index, image.image_url)
      )
    } catch (err) {
      console.error('Erro ao baixar imagem:', err)
      setError('Não foi possível baixar esta foto.')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDownloadAll = async () => {
    if (!images.length || zipping || downloadingId) return
    setZipping(true)
    setError(null)
    try {
      await downloadPhotosZip(images, zipFileName(fileLabel), fileLabel)
      showToast(
        images.length === 1 ? 'Foto baixada.' : `${images.length} fotos baixadas em um zip.`,
        'success'
      )
    } catch (err) {
      console.error('Erro ao baixar fotos:', err)
      setError('Não foi possível baixar as fotos.')
    } finally {
      setZipping(false)
    }
  }

  if (loading) {
    return (
      <div className="product-image-manager loading">
        <Loader2 className="loading-icon" size={24} />
        <span>Carregando imagens...</span>
      </div>
    )
  }

  return (
    <div className="product-image-manager">
      <div className="product-image-manager-header">
        <div>
          <h3>Imagens do Produto</h3>
          <p className="product-image-manager-hint">
            {images.length === 0
              ? editable
                ? 'Nenhuma foto ainda — arraste várias de uma vez ou selecione no computador.'
                : 'Nenhuma foto neste produto.'
              : editable
                ? `${images.length} foto${images.length === 1 ? '' : 's'} · arraste mais arquivos para adicionar`
                : `${images.length} foto${images.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="product-image-manager-actions">
          {images.length > 0 ? (
            <button
              type="button"
              className="btn-download-all"
              onClick={handleDownloadAll}
              disabled={zipping || downloadingId != null || uploading}
            >
              {zipping ? <Loader2 className="loading-icon" size={16} /> : <Download size={16} />}
              <span>{zipping ? 'Preparando zip...' : 'Baixar todas'}</span>
            </button>
          ) : null}
        {editable ? (
          <label className={`btn-upload-image ${uploading ? 'is-disabled' : ''}`}>
            <Upload size={16} />
            <span>Adicionar imagens</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        ) : null}
        </div>
      </div>

      {error ? (
        <div className="product-image-manager-error">{error}</div>
      ) : null}

      {uploading ? (
        <div className="product-image-manager-uploading">
          <Loader2 className="loading-icon" size={16} />
          <span>
            {uploadProgress
              ? `Enviando ${uploadProgress.current} de ${uploadProgress.total}: ${uploadProgress.name}`
              : 'Enviando imagens...'}
          </span>
        </div>
      ) : null}

      {editable ? (
        <div
          className={`product-image-dropzone ${dragActive ? 'is-active' : ''} ${
            uploading ? 'is-disabled' : ''
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => {
            if (!uploading) fileInputRef.current?.click()
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (!uploading) fileInputRef.current?.click()
            }
          }}
        >
          <Upload size={28} className="dropzone-icon" aria-hidden />
          <p className="dropzone-title">Arraste uma ou várias fotos aqui</p>
          <p className="dropzone-subtitle">
            ou clique para selecionar · JPG, PNG, WEBP · até 5MB cada
          </p>
        </div>
      ) : null}

      {images.length === 0 ? (
        <div className="product-image-manager-empty">
          <ImageIcon size={48} className="empty-icon" />
          <p>Nenhuma imagem adicionada ainda</p>
        </div>
      ) : (
        <div className="product-images-grid">
          {images.map((image, index) => (
            <div key={image.id} className="product-image-item">
              <div className="product-image-wrapper">
                <img
                  src={image.image_url}
                  alt={`Imagem ${index + 1} do produto`}
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                <div className="image-placeholder" style={{ display: 'none' }}>
                  <ImageIcon size={32} />
                  <span>Erro ao carregar</span>
                </div>

                <button
                  type="button"
                  className="btn-image-download"
                  onClick={() => handleDownloadOne(image, index)}
                  disabled={downloadingId != null || zipping}
                  title="Baixar esta foto"
                  aria-label={`Baixar foto ${index + 1}`}
                >
                  {downloadingId === image.id ? (
                    <Loader2 className="loading-icon" size={16} />
                  ) : (
                    <Download size={16} />
                  )}
                </button>
                {editable ? (
                  <div className="product-image-actions">
                    <button
                      type="button"
                      className="btn-image-action"
                      onClick={() => handleMoveImage(image.id, 'up')}
                      disabled={index === 0}
                      title="Mover para cima"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      className="btn-image-action"
                      onClick={() => handleMoveImage(image.id, 'down')}
                      disabled={index === images.length - 1}
                      title="Mover para baixo"
                    >
                      <ArrowDown size={16} />
                    </button>
                    <button
                      type="button"
                      className="btn-image-action btn-image-delete"
                      onClick={() => handleDeleteImage(image.id)}
                      title="Remover imagem"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="product-image-order">Imagem {index + 1}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProductImageManager
